import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axiosInstance from '@/axios'
import { useConfig } from '@/composables/useConfig'

export const useAuthStore = defineStore('auth', () => {
  // Obtener configuración
  const { config, loadConfig } = useConfig()
  
  // Estado reactivo
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token'))
  const expiresAt = ref(localStorage.getItem('token_expires_at'))
  const loading = ref(false)
  const refreshTimer = ref(null)
  const checkTimer = ref(null)

  // Getters computados
  const isAuthenticated = computed(() => {
    return !!(token.value && !isTokenExpired())
  })

  const isLoggedIn = computed(() => isAuthenticated.value)

  const userInfo = computed(() => user.value)

  const tokenExpiry = computed(() => {
    if (!expiresAt.value) return null
    return new Date(expiresAt.value)
  })

  const timeUntilExpiry = computed(() => {
    if (!expiresAt.value) return 0
    const expiry = new Date(expiresAt.value)
    const now = new Date()
    return Math.max(0, expiry.getTime() - now.getTime())
  })

  const minutesUntilExpiry = computed(() => {
    return Math.floor(timeUntilExpiry.value / (1000 * 60))
  })

  // Funciones auxiliares
  const isTokenExpired = () => {
    if (!expiresAt.value) return true
    const expiry = new Date(expiresAt.value)
    const now = new Date()
    return now >= expiry
  }

  const shouldRefreshToken = () => {
    if (!expiresAt.value) return false
    const expiry = new Date(expiresAt.value)
    const now = new Date()
    const timeUntilExpiry = expiry.getTime() - now.getTime()
    // Renovar si quedan menos del tiempo configurado
    return timeUntilExpiry < config.auth.tokenRenewalThreshold
  }

  const saveTokenData = (tokenData) => {
    token.value = tokenData.token
    expiresAt.value = tokenData.expires_at
    user.value = tokenData.user

    localStorage.setItem('auth_token', tokenData.token)
    localStorage.setItem('token_expires_at', tokenData.expires_at)
    localStorage.setItem('user_data', JSON.stringify(tokenData.user))
  }

  const clearTokenData = () => {
    token.value = null
    expiresAt.value = null
    user.value = null

    localStorage.removeItem('auth_token')
    localStorage.removeItem('token_expires_at')
    localStorage.removeItem('user_data')
  }

  const clearTimers = () => {
    if (refreshTimer.value) {
      clearInterval(refreshTimer.value)
      refreshTimer.value = null
    }
    if (checkTimer.value) {
      clearInterval(checkTimer.value)
      checkTimer.value = null
    }
  }

  const startTokenMonitoring = () => {
    clearTimers()

    // Verificar token según el intervalo configurado
    checkTimer.value = setInterval(async () => {
      if (isAuthenticated.value) {
        await checkToken()
      }
    }, config.auth.tokenCheckInterval)

    // Renovar token automáticamente si es necesario
    refreshTimer.value = setInterval(async () => {
      if (isAuthenticated.value && shouldRefreshToken()) {
        await refreshToken()
      }
    }, config.auth.tokenRenewalInterval)
  }

  // Acciones principales
  const login = async (credentials) => {
    loading.value = true
    try {
      const response = await axiosInstance.post('/api/login', credentials)
      const tokenData = response.data

      saveTokenData(tokenData)
      startTokenMonitoring()

      return { success: true, data: tokenData }
    } catch (error) {
      clearTokenData()
      return { 
        success: false, 
        error: error.response?.data?.message || 'Error al iniciar sesión' 
      }
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    loading.value = true
    try {
      if (token.value) {
        await axiosInstance.post('/api/logout')
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error)
    } finally {
      clearTokenData()
      clearTimers()
      loading.value = false
    }
  }

  const checkToken = async () => {
    if (!token.value) return false

    try {
      const response = await axiosInstance.get('/api/check-token')
      return response.data.valid
    } catch (error) {
      if (error.response?.status === 401) {
        // Token inválido, cerrar sesión inmediatamente
        console.log('Token inválido detectado en check-token, cerrando sesión...')
        await logout()
        return false
      }
      return false
    }
  }

  const refreshToken = async () => {
    if (!token.value) return false

    try {
      const response = await axiosInstance.post('/api/refresh-token')
      const tokenData = response.data

      saveTokenData(tokenData)
      return true
    } catch (error) {
      console.error('Error al renovar token:', error)
      // Si no se puede renovar, cerrar sesión
      await logout()
      return false
    }
  }

  const getCurrentUser = async () => {
    if (!token.value) return null

    try {
      const response = await axiosInstance.get('/api/user')
      user.value = response.data
      localStorage.setItem('user_data', JSON.stringify(response.data))
      return response.data
    } catch (error) {
      console.error('Error al obtener usuario:', error)
      return null
    }
  }

  const initializeAuth = async () => {
    // Esperar a que la configuración se cargue
    await loadConfig()
    
    // Cargar datos del localStorage si existen
    const savedUser = localStorage.getItem('user_data')
    if (savedUser) {
      try {
        user.value = JSON.parse(savedUser)
      } catch (error) {
        console.error('Error al parsear datos de usuario:', error)
      }
    }

    // Verificar si el token es válido
    if (token.value && !isTokenExpired()) {
      // Verificar con el servidor
      const isValid = await checkToken()
      if (isValid) {
        startTokenMonitoring()
        return true
      }
    }

    // Si no es válido, limpiar datos
    clearTokenData()
    return false
  }

  return {
    // Estado
    user,
    token,
    expiresAt,
    loading,
    
    // Getters
    isAuthenticated,
    isLoggedIn,
    userInfo,
    tokenExpiry,
    timeUntilExpiry,
    minutesUntilExpiry,
    
    // Acciones
    login,
    logout,
    checkToken,
    refreshToken,
    getCurrentUser,
    initializeAuth,
    startTokenMonitoring,
    clearTokenData,
    clearTimers
  }
})
